import { useState, useCallback, useRef } from 'react'
import { AccessTokenPayload } from '@mondaygpt/types'
import { jwtDecode } from 'jwt-decode'
import { OpenAPI } from 'openapi'
import { getDeviceId } from 'modules/core'

const CLI_PORT_KEY = 'cli_port'
const CLI_STATE_KEY = 'cli_state'

/**
 * Captures cli_port and cli_state from URL search params into sessionStorage.
 * Runs synchronously during render (via useRef guard) so sessionStorage is
 * populated before useCliAuthCallback reads it.
 */
export function useCliParamsCapture() {
  const initialized = useRef(false)
  if (!initialized.current) {
    initialized.current = true
    const params = new URLSearchParams(window.location.search)
    const cliPort = params.get('cli_port')
    const cliState = params.get('cli_state')
    const port = parseInt(cliPort ?? '', 10)
    if (port >= 1 && port <= 65535) {
      sessionStorage.setItem(CLI_PORT_KEY, String(port))
      if (cliState) sessionStorage.setItem(CLI_STATE_KEY, cliState)
    }
  }
}

/**
 * Returns CLI auth callback utilities.
 * - isCliMode: whether we're in a CLI signup flow (captured once, stable across re-renders)
 * - sendTokensToCli: requests a fresh, CLI-scoped token pair from the server and POSTs
 *   it to the CLI's local server. The browser session no longer has direct access to
 *   the refresh token (it lives in an HttpOnly cookie), so we ask the server to mint
 *   a separate device session for the CLI install. Called via raw fetch because the
 *   endpoint is brand new and not yet in the autogenerated OpenAPI client.
 */
export function useCliAuthCallback() {
  const [isCliMode] = useState(() => !!sessionStorage.getItem(CLI_PORT_KEY))

  const sendTokensToCli = useCallback(async (): Promise<boolean> => {
    const cliPort = sessionStorage.getItem(CLI_PORT_KEY)
    const cliState = sessionStorage.getItem(CLI_STATE_KEY)
    if (!cliPort) return false

    try {
      const accessToken = typeof OpenAPI.TOKEN === 'string' ? OpenAPI.TOKEN : undefined
      if (!accessToken) return false

      // CLI-scoped deviceId so the CLI session can be revoked independently of the
      // browser session in the future "active sessions" UI.
      const cliDeviceId = `cli:${getDeviceId()}`

      const issueRes = await fetch(`${OpenAPI.BASE}/v1/authentication/cli/issue-tokens`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          'X-Client-Type': 'web',
        },
        body: JSON.stringify({ deviceId: cliDeviceId }),
      })
      if (!issueRes.ok) return false

      const tokens = (await issueRes.json()) as {
        accessToken: string
        refreshToken: string
        businessId: string
      }
      const payload = jwtDecode<AccessTokenPayload>(tokens.accessToken)

      const res = await fetch(`http://127.0.0.1:${cliPort}/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          businessId: payload.bid,
          state: cliState,
        }),
      })
      return res.ok
    } catch {
      return false
    } finally {
      sessionStorage.removeItem(CLI_PORT_KEY)
      sessionStorage.removeItem(CLI_STATE_KEY)
    }
  }, [])

  return { isCliMode, sendTokensToCli }
}
